import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  getListConversationsApiV1ConversationsGetQueryKey,
  useConversationActionApiV1ConversationsActionPost,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { ConversationActionType } from '#/shared/api/generated/communication/models'

import { findCachedConversation } from '../lib/cached-conversations'
import type { ConversationTarget } from './conversation'

interface MarkReadOptions {
  /**
   * Mark read even when the cache shows no unread items. Used while the thread
   * is open and a new message streams in — the user is looking at it, so its
   * last-read must advance even before the list cache reflects the new unread.
   */
  force?: boolean
}

/**
 * Marks the open thread read on the server (clearing its unread count + tab
 * badges) and refreshes the list. The `read` action needs a conversation id —
 * taken from the target when id-scoped, else resolved against the loaded
 * conversation lists. Without `force` the request is skipped when the thread has
 * no unread items (and phone-only targets missing from the cache are left
 * alone). Fire-and-forget and silent.
 */
export function useMarkConversationRead(): (
  target: ConversationTarget,
  options?: MarkReadOptions,
) => void {
  const queryClient = useQueryClient()
  const { mutate } = useConversationActionApiV1ConversationsActionPost()

  return useCallback(
    (target: ConversationTarget, options?: MarkReadOptions) => {
      const conversation = findCachedConversation(queryClient, target)
      const conversationId = target.conversationId ?? conversation?.id
      if (!conversationId) return
      if (!options?.force && !conversation?.unread_count) return
      mutate(
        {
          data: {
            action: ConversationActionType.read,
            conversation_id: conversationId,
          },
        },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: getListConversationsApiV1ConversationsGetQueryKey(),
            })
          },
        },
      )
    },
    [mutate, queryClient],
  )
}
